<template>
    <div class="container">
        <h1>Les défis de Cardano</h1>
        <p class="intro">
            Si Cardano est aujourd'hui une cryptomonnaie qui sent bon, ça ne veut pas dire que la route vers le top1 sera un long fleuve tranquille.
            Analyse des difficultés sur le chemin.
        </p>

        <p>
            La cryptosphère est un milieu où se rencontrent 2 mondes: <b>le monde de l'ingienérie</b> et <b>le monde de la finance</b>.
            Le premier cherche à fournir des solutions techniques au second, mais le second fonctionne selon ses propres codes et ses propres
            règles.
        </p>

        <p>
            Aussi, vous pourriez inventer la meilleure cryptomonnaie qui soit (techniquement) que vous n'auriez fait que la moitié du chemin: il
            faut convaincre suffisamment de monde (particulier, entreprises, institutionnels) de s'y engager afin d'avoir une cryptomonnaie
            fonctionnelle et précieuse. A l'inverse, un certain nombre de <b>shitcoins</b> ont réussi à convaincre suffisamment de monde aujourd'hui
            pour être capitalisés à plusieurs milliards de dollars !
        </p>

        <p>
            Dans cet article, nous allons différencier 2 classes de défis à relever pour Cardano, les <b>défis techniques</b> et les
            <b>défis financiers</b>
        </p>

        <h2>Les défis techniques de Cardano</h2>
        <p>
            Voici l'inventaire des défis à relever pour Cardano afin de pouvoir prétendre à devenir une monnaie globale.
        </p>

        <h3>Les TPS (Transactions Par Secondes)</h3>
        <p>
            Imaginez un monde dans lequel chacun ferait abstraction des banques et pourrait payer directement depuis la blockchain.
            Et bien, Bitcoin ne peut pas remplir cette fonction pour vous: Si c'était le cas, <b>vous resteriez planté une dizaine de minutes
                devant la caisse à chaque fois que vous utiliseriez votre portefeuille</b>. Notez que vous pouvez payer en Bitcoin aujourd'hui,
            Les plateformes d'exchanges (Binance, Crypto.com, etc) ainsi que bien d'autres intermédiaires proposent des cartes de crédit aujourd'hui,
            mais le problème est bien là: ils sont intermédiaires. Si on essaye de remplacer les banques par des blockchain, c'est bien pour supprimer
            les intermédiaires, pas pour les remplacer par d'autres. Car au fond, pourquoi faire plus confiance à Binance qu'à votre banque ?
        </p>

        <p>
            Si Bitcoin est difficile à utiliser (sans intermédiaire) dans la vie de tous les jours, c'est parce que le réseau n'est pas assez performant.
            Le réseau Bitcoin supporte 4 TPS (transactions par secondes), là ou VISA a un réseau qui en traine en moyenne 1700 par seconde, et qui
            peut monter jusqu'à 24000. Le problème d'ordre de grandeur est choquant: <b>Bitcoin doit devenir environ 1000x plus performant s'il veut
                prétendre à devenir une monnaie globale</b> (en tout cas, au sens de Satoshi Nakamoto). Ethereum a aussi un long chemin devant lui,
            il tourne à 15 TPS.
        </p>

        <p>
            Certaines cryptomonnaies affichent de meilleures performances, (comme Ripple, Stellar ou EOS qui dépassent les 1000 TPS), mais
            elles ont vendues leur âme au diable pour cela: <b>Elles sont assez, voir fortement centralisées</b>. Le challenge ici ne réside pas simplement dans un nombre de transaction par seconde élevées, mais dans un nombre élevé de TPS
            <u>sans sacrifier la décentralisation</u>.
        </p>

        <p>
            La bonne nouvelle, c'est que Cardano a sorti le 25 mars 2020 la première implémentation de son protocole <b>Hydra</b>, un protocole
            de niveau deux (couche 2) capable de traiter théoriquement 1000 TPS par utilisateur connecté, soit plus d'1 millions de TPS
            pour le réseau actuel (de 1000+ stake pools).
        </p>

        <p>
            L'autre bonne nouvelle, c'est que <b>Hydra</b> est compatible avec toutes les optimisations futures prévues pour l'ère Basho, en particulier
            avec le sharding.
        </p>

        <p>
            Le défi pour Cardano ne semble pas tant être <b>d'atteindre</b>, mais plutôt de <b>conserver</b> un nombre de transactions par secondes élevée
            avec l'implémentation des différentes fonctionnalités, en particulier celles de l'ère Goguen (les smart contracts).
        </p>

        <h3>Scalabilité des données</h3>
        <p>
            Celui-ci est facile à comprendre. Les blockchains sont éternelles. Mais si elles ne cessent de grandir, et que tous les opérateurs et intervenant
            du réseau doivent en stocker une copie complète, ne risque-t'on pas de rapidement atteindre les limites de stockages des machines ?
        </p>

        <p>Plusieurs solutions à ce problèmes seront (ou sont) développées par Cardano:</p>
        <ul>
            <li>
                le <mark>sharding</mark>, permettant de n'envoyer qu'une partie de la blockchain à chaque noeud. Les différents noeuds
                pourront interroger la blockchain gràce à un système de <a href="https://fr.wikipedia.org/wiki/Publish-subscribe">
                    publication/souscription</a>. L'ensemble de la blockchain sera stockée sur le réseau (répliquées maintes fois), mais aucun noeud
                n'aura besoin de la stocker dans son intégralité.
            </li>
            <li>
                Le <mark>pruning</mark>: une simplification des jeux de données pour minimiser leur taille.
            </li>
            <li>
                La compression des données.
            </li>
        </ul>

        <h3>Scalabilité du réseau</h3>
        <p>
            Un facteur important dans la croissance d'un réseau est la bande passante. En effet, la blockchain est un jeu de données qui ne fera
            que grandir, et cette blockchain est répliquée sur tous les noeuds. Si l'adoption de Cardano s'accélère, de plus en plus de données
            devront être propagées sur le réseau, et la bande passante pourrait alors devenir le réactif limitant du réseau.
        </p>

        <p>
            Cardano anticipe 2 solutions permettant d'alléger les transferts de données:
        </p>
        <ul>
            <li>
                le <mark>sharding</mark>, comme vu précédemment. Si on n'envoit qu'une partie de la blockchain à chaque noeud, cela allègera également
                considérablement la charge sur le réseau également.
            </li>
            <li>
                le protocole <mark>RINA</mark> (<a href="https://en.wikipedia.org/wiki/Recursive_Internetwork_Architecture">
                    Recursive InterNetwork Architecture</a>).
                L'idée de crée et d'utiliser cette couche réseau récursive est de pouvoir réduire le quantité de données envoyées sur le réseau
                pour supporter les différentes couches de transports tout en conservant la transparence et la sécurité nécessaire au fonctionnement
                d'une blockchain.
            </li>
        </ul>

        <h2>Les défis financiers de Cardano</h2>
        <p>
            Cardano a déjà réussi à se hisser dans le top10 des cryptomonnaies, avec une communauté de près de <b>500 000</b> membres.
            Mais, les institutionnels (hedge funds, états) n'ont qu'un mot à la bouche lorsqu'on parle de Cryptomonnaie: <b>Bitcoin</b>.
            Avoir la meilleure blockchain au monde ne suffit pas nécessairement à une adoption majeure, il faut aussi convaincre.
        </p>

        <h3>Absorber les smarts contracts</h3>
        <p>
            En réalité, le principal concurrent de Cardano (pour le moment) n'est pas Bitcoin, c'est <b>Ethereum</b>. Principale plateforme de
            smart-contracts, Ethereum est capitalisée à 50 milliards de dollars, soit plus de 15x la capitalisation actuelle de Cardano.
        </p>

        <p>
            Alors que la plateforme Ethereum peine à traiter les transactions (TPS faible, frais élevées), la phase <b>Goguen</b> de Cardano est
            dans les cartons pour supporter les smarts contracts. Si on ne sait pas encore quelle sera la désirabilité de Cardano pour des financiers,
            on a moins de doutes pour les ingénieurs qui concoivent des applications décentralisées: Cardano est déjà réputé pour être une plateforme
            fiable, grâce à <b>la revue par les pairs</b> et la <b>preuve de code</b>.
        </p>

        <p>
            La plateforme <b>Plutus</b> de création de contrat intelligent devrait très vite afficher son intérêt: basée sur Haskell, elle permettra,
            elle-aussi, les preuves de code, ce qui est un avantage indiscutable: lorsqu'on manipule des milliers ou des millions d'euros de flux
            financier, on est nettement plus serein quand on a <u>prouvé</u> que notre code va se comporter de façon attendue.
        </p>

        <p>
            Les premiers à sauter le pas pourront être vraisemblablement les <b>stablecoins</b> comme Thether, DAI, USDC, XAUT, etc. Qu'ils soient indexé
            sur le dollar, l'euro, le yen ou encore l'or, les stablecoins ont tendances à se répandre sur différentes plateformes de smart contracts
            pour diminuer les risques. Ils reposent sur des contrats intelligents, et il faut que ces contrats s'exécutent pour maintenir la valeur du
            jeton au taux voulu, et ce coûte que coûte.
        </p>

        <p>
            La finance décentralisée et les casinos en lignes devraient suivre le pas, eux aussi guidés par des intérêts financiers énormes. La suite
            se fera plus lentement et naturellement: jeux en lignes, applications médicales, tracabilité: une fois un certain nombre d'utilisateurs
            installés, les ressources (tutoriels, vidéos, base de code, questions/réponses sur stackoverflow) se construisent sur la durée pour faciliter
            l'absorption de nouveaux utilisateurs.
        </p>

        <h3>Absorber l'industrie</h3>
        <p>
            L'industrie pourrait y trouver très rapidement de multiples avantage.
        </p>

        <p>
            Par exemple, plutôt que de distribuer directement des actions sur les marchés d'action, les entreprises seront en mesure d'emettre
            directement des jetons de leur cryptomonnaie, supporté nativement par la plateforme Cardano. Il suffit d'allouer un certain nombre d'actions
            aux détenteurs des jetons, et n'importe qui peut se retrouver à investir n'importe quelle somme, alors qu'une action Alphabet (Google) coûte
            aujourd'hui 1754$ et celle d'Amazon coûte 3113$. D'autre part, cette solution permettrait de s'affranchir des intermédiaires (courtiers,
            places de marchés, etc).
        </p>

        <p>
            Une autre utilisation possible de la blockchain est de se créer des assurances sur mesure avec les smarts contracts.
            Vous pouvez, par exemple, offrir des smarts contracts pour garantir vos récoltes:
        </p>
        <pre>
            <quote>
                - Vous gagnez 10 ADAs par jour jusqu'au 13 avril 2022
                - S'il fait plus de 30°C pendant plus de 10 jours d'affilée, vous payez 3000 ADAs
            </quote>
        </pre>

        <p>
            Vos contrats sont publiés sur la blockchain. Faîtes vos calculs, publiez, disons, 50 contrats comme celui-ci, et voila, vous avez assuré
            vos récoltes sans remplir les poches d'intermédiaires au dents longues. La bonne vieille règle de l'offre et de la demande pourra avoir
            raison des assureurs les moins compétitifs.
        </p>

        <h3>Absorber la finance</h3>
        <p>
            Si <b>Plutus</b> peut absorber les ingénieurs de smarts contracts, le monde de la finance classique n'est, lui, pas nécessairement
            familier avec l'ingénierie et la programmation. C'est <mark>Marlowe</mark> et le <mark>Marlowe Playground</mark> qui auront pour
            mission de les intéresser. Si le premier est une simplification et une spécialisation financière du langage Plutus, pour le réduire
            à son minimum, le second sera une plateforme de <b>programmation visuelle</b>, permettant aux banquiers, investisseurs, comptables
            et autres non-tech de pouvoir utiliser les smart contracts sans avoir besoin de connaissances en programmation.
        </p>

        <p>
            L'avantage de cette offre est que les différents produits financiers (titres, actions, produits dérivés, assurances sur mesure, etc)
            pourront être directement créés et garantis par la blockchain, représentant alors un <b>meilleur rapport rendement/risque</b> que les
            produits existants, qui doivent être garantis (ou contre-garantis) par des entités (particuliers, entreprises, états) sur lesquelles
            subsisent toujours un risque de <b>non-solvabilité</b>.
        </p>

        <p>
            Si le monde de la finance s'engage sur Cardano via cette porte ouverte, la blockchain Cardano atteindrait une capitalisation encore jamais
            vue dans la cryptosphère. Pour comprendre l'échelle, le marché global des cryptomonnaies est de quelques 461 milliards de dollars au moment
            ou j'écris cet article, alors que <b>celui des produits dérivé est de 558 000 milliards</b>.
        </p>

        <h3>Décentraliser Internet</h3>
        <p>
            Cardano est un projet ambitieux. Il cherche à poser les fondations d'un Internet totalement décentralisé sur laquel pourrait s'appuyer
            les relations et échanges entre humains. Si le monde économique a fait de l'internet ce qu'il est aujourd'hui, un certain nombre d'acteurs
            ont absorbé une puissance inouïe, en ayant réussi à se positionner comme incontournable sur les marchés qu'ils exploitent.
        </p>

        <p>
            S'il y a bien un marché que les blockchains peuvent disrupter, c'est bien le <b>marché de la confiance</b> dans son ensemble. Banques,
            courtiers, n'importe quel acteur qui est rémunéré pour assurer une transaction n'aura plus besoin de l'être dans le futur, car une
            blockchain finira par prendre le relai: vous serez toujours plus serein de n'avoir fait confiance à personne qu'a une entité individuelle.
        </p>

        <p>
            Si Cardano est parfois moqué pour son ambition démesurée, la roadmap est claire: petit pas par petit pas, le projet avance prudemment
            vers sa prochaine étape. Seul le futur nous dira où il s'arrêtera.
        </p>

        <div class="button-line">
            <a class="btn btn-outline-primary btn-left" href="/doc-cardano/les-phases-de-cardano-roadmap">
                <i class="fa fa-chevron-left"></i>
                <i>Précédent:</i>
                <b>Les phases de Cardano</b>
            </a>
        </div>
    </div>
</template>

<script>
import createHeader from "../../js/meta.js";

export default {
    layout: "DocLayout",
    head: createHeader({
        title: "Le objectifs et défis de Cardano - Stakepool.fr",
        description:
            "Cardano est une blockchain ambitieuse. Découvrez ses objectifs, et les défi à relever pour qu'elle les atteigne"
    })
};
</script>